import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/empty-state";
import { EntityErrorState } from "@/components/layout/entity-state";
import { LimitReachedBanner } from "@/components/limit-reached-banner";
import { useCanEdit, useEditorCopy } from "@/lib/project-access";
import {
  FilterBar,
  FilterPill,
  AddFilterMenu,
} from "@/components/ui/filter-bar";
import { TableToolbar } from "@/components/ui/table-toolbar";
import { TableSearchInput } from "@/components/ui/table-search-input";
import { FilteredEmptyState } from "@/components/ui/filtered-empty-state";
import { useProject } from "@/components/project-context";
import { WalkthroughItemContent } from "@/components/experience/walkthrough-item.js";
import { RunTrend } from "@/components/experience/run-trend";
import { type ProblemCounts, numberRuns, runTime } from "@/lib/run-trend";
import { features } from "@/lib/features";
import {
  criticalityLabel as vocabCriticalityLabel,
  criticalityTone,
} from "@/components/experience/vocabulary.js";
import { Chip } from "@/components/ui/chip";
import { toneChipTone } from "@/components/walkthrough-badges";
import {
  PlayCircle,
  FileText,
  Check,
  Copy,
  Download,
  Trash2,
  MoreHorizontal,
  GitCompare,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { BatchActionBar } from "@/components/batch-action-bar";
import { PersonaRow } from "@/components/experience/persona-row";
import { ScenarioRow } from "@/components/experience/scenario-row";
import { SortDropdown } from "@/components/ui/sort-dropdown.js";
import { WalkthroughsTabs } from "@/components/gate/walkthroughs-tabs";
import { toast } from "sonner";

const WALKTHROUGH_SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
] as const;

// ── Helpers ─────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Types ──────────────────────────────────────────────────────

type WalkthroughRow = {
  id: string;
  scenarioId: string;
  outlineId: string;
  scenarioName: string;
  scenarioTension: string | null;
  scenarioTaskCriticality: string | null;
  personaId: string | null;
  personaName: string | null;
  buildId?: string | null;
  buildLabel?: string | null;
  mode: string;
  status: string;
  verdict: Array<{ rating: string; reasoning?: string }> | null;
  findingCount: number;
  severityCounts: Record<string, number>;
  problemCounts?: ProblemCounts | null;
  stepCount: number;
  completedAt: string | null;
  startedAt: string | null;
  createdAt: string;
  models?: {
    walker?: string;
    judge?: string;
    matcher?: string;
    walker_reported?: string;
    judge_reported?: string;
    velocity_sec_per_step?: number;
    total_duration_sec?: number;
  } | null;
};

type ScenarioPersona = {
  id: string | null;
  name: string;
};

type ScenarioGroup = {
  scenarioId: string;
  outlineId: string;
  scenarioName: string;
  scenarioTension: string | null;
  scenarioTaskCriticality: string | null;
  personas: ScenarioPersona[];
  walkthroughs: WalkthroughRow[];
  latestVerdict: string | null;
  mostRecentDate: number;
};

// ── Helpers ────────────────────────────────────────────────────

// ── Scenario Grouping ──────────────────────────────────────────

function groupByScenario(walkthroughs: WalkthroughRow[]): ScenarioGroup[] {
  const groupMap = new Map<string, ScenarioGroup>();

  for (const wt of walkthroughs) {
    let group = groupMap.get(wt.scenarioId);
    if (!group) {
      group = {
        scenarioId: wt.scenarioId,
        outlineId: wt.outlineId,
        scenarioName: wt.scenarioName,
        scenarioTension: wt.scenarioTension,
        scenarioTaskCriticality: wt.scenarioTaskCriticality,
        personas: [],
        walkthroughs: [],
        latestVerdict: null,
        mostRecentDate: 0,
      };
      groupMap.set(wt.scenarioId, group);
    }
    group.walkthroughs.push(wt);
    if (wt.personaName) {
      const existing = group.personas.find(
        (p) =>
          (wt.personaId && p.id === wt.personaId) || p.name === wt.personaName,
      );
      if (!existing) {
        group.personas.push({
          id: wt.personaId ?? null,
          name: wt.personaName,
        });
      }
    }
  }

  // For each group: sort walkthroughs by createdAt DESC, find latest verdict, compute most recent date
  for (const group of groupMap.values()) {
    group.walkthroughs.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    // Latest verdict: from the most recently completed walkthrough that has a verdict
    for (const wt of group.walkthroughs) {
      if (wt.verdict && wt.verdict.length > 0) {
        group.latestVerdict = wt.verdict[0].reasoning ?? null;
        break;
      }
    }

    // Most recent date for sorting groups
    const newest = group.walkthroughs[0];
    if (newest) {
      group.mostRecentDate = runTime(newest);
    }
  }

  // Sort groups by most recent walkthrough timestamp (most recently active first)
  const groups = Array.from(groupMap.values());
  groups.sort((a, b) => b.mostRecentDate - a.mostRecentDate);

  return groups;
}

// ── Release Summary Content ─────────────────────────────────────

function ReleaseSummaryContent({
  walkthroughIds,
  projectId,
}: {
  walkthroughIds: string[];
  projectId: string;
}) {
  const { data, isLoading, error, refetch } =
    trpc.walkthrough.generateReleaseSummary.useQuery(
      { projectId, walkthroughIds },
      { enabled: walkthroughIds.length >= 2 },
    );

  if (isLoading) {
    return (
      <div className="space-y-3 py-8">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6 flex flex-col items-start gap-3">
        <p className="text-destructive text-sm font-medium">
          Failed to generate release summary.
        </p>
        <p className="text-muted-foreground text-xs">
          An error occurred while compiling the latest release notes.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!data) return null;

  function handleCopy() {
    if (!data) return;
    navigator.clipboard.writeText(data.verdictMarkdown).then(
      () => toast.success("Summary copied to clipboard"),
      () => toast.error("Failed to copy"),
    );
  }

  function handleDownloadHtml() {
    if (!data) return;
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Release Summary</title>
  <style>
    body { font-family: system-ui, sans-serif; max-width: 700px; margin: 2rem auto; padding: 0 1rem; color: #1a1a1a; }
    h1 { font-size: 1.5rem; margin-bottom: 1.5rem; }
    h2 { font-size: 1.1rem; margin-top: 1.5rem; margin-bottom: 0.5rem; }
    h3 { font-size: 1rem; margin-top: 1rem; margin-bottom: 0.5rem; }
    .badge { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 0.25rem; font-size: 0.75rem; border: 1px solid #ddd; margin-right: 0.25rem; }
    .finding { border: 1px solid #e5e5e5; border-radius: 0.5rem; padding: 0.75rem; margin-bottom: 0.5rem; }
    .finding p { margin: 0.25rem 0; font-size: 0.875rem; }
    .meta { color: #666; font-size: 0.75rem; }
    .persona-item { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; margin: 0.25rem 0; }
  </style>
</head>
<body>
  <h1>Release Summary</h1>
  <p class="meta">${data.walkthroughCount} walkthroughs, ${data.uniqueFindingCount} unique findings (${data.totalFindingCount} total observations)</p>

  <h2>Severity Breakdown</h2>
  <div>
    ${Object.entries(data.severityBreakdown)
      .filter(([, c]) => c > 0)
      .map(([s, c]) => `<span class="badge">${c} ${escapeHtml(s)}</span>`)
      .join(" ")}
  </div>

  <h2>Persona Coverage</h2>
  <div>
    ${data.personaCoverage.map((p) => `<div class="persona-item">${escapeHtml(p.personaName)} &mdash; ${escapeHtml(p.scenarioName)}</div>`).join("")}
  </div>

  ${data.verdictHtml}

  <h2>Findings (${data.findings.length})</h2>
  ${data.findings
    .map(
      (f) => `
  <div class="finding">
    <div><span class="badge">${escapeHtml(f.severity)}</span><span class="badge">${escapeHtml(f.type)}</span>${f.observationCount > 1 ? `<span class="meta">Seen ${f.observationCount}x</span>` : ""}</div>
    <p>${escapeHtml(f.description)}</p>
  </div>`,
    )
    .join("")}
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "release-summary.html";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6 py-4">
      {/* Actions */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleCopy}>
          <Copy className="mr-2 h-4 w-4" />
          Copy Markdown
        </Button>
        <Button variant="outline" size="sm" onClick={handleDownloadHtml}>
          <Download className="mr-2 h-4 w-4" />
          Download HTML
        </Button>
      </div>

      {/* Severity breakdown */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.severityBreakdown).map(
          ([sev, count]) =>
            count > 0 && (
              <Badge key={sev} variant="outline">
                {count} {sev}
              </Badge>
            ),
        )}
      </div>

      {/* Persona coverage */}
      <section>
        <h2 className="text-sm font-medium mb-2">Persona Coverage</h2>
        <div className="space-y-1">
          {data.personaCoverage.map((p) => (
            <div
              key={p.walkthroughId}
              className="flex items-center gap-2 text-sm"
            >
              <Check
                className="h-3.5 w-3.5 text-finding-helps shrink-0"
                aria-hidden="true"
              />
              <PersonaRow
                variant="inline"
                persona={{
                  id: p.personaId ?? p.personaName,
                  name: p.personaName,
                }}
                href={
                  p.personaId
                    ? `/projects/${projectId}/personas/${p.personaId}`
                    : undefined
                }
                size="xs"
              />
              <span className="text-xs text-muted-foreground">--</span>
              <ScenarioRow
                variant="inline"
                scenario={{ id: p.scenarioId, name: p.scenarioName }}
                href={`/projects/${projectId}/scenarios/${p.scenarioId}`}
                className="text-xs"
              />
            </div>
          ))}
        </div>
      </section>

      {/* AI-synthesized verdict */}
      <section>
        <h2 className="text-sm font-medium mb-2">Verdict</h2>
        <div
          className="prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: data.verdictHtml }}
        />
      </section>

      {/* Deduplicated findings */}
      <section>
        <h2 className="text-sm font-medium mb-2">
          Findings ({data.findings.length})
        </h2>
        <div className="space-y-2">
          {data.findings.map((f) => (
            <div key={f.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Badge variant="outline" className="text-xs">
                  {f.severity}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {f.type}
                </Badge>
                {f.observationCount > 1 && (
                  <span className="text-xs text-muted-foreground">
                    Seen {f.observationCount}x
                  </span>
                )}
              </div>
              <p className="text-sm">{f.description}</p>
              {f.recommendation && (
                <p className="text-xs text-muted-foreground mt-1">
                  Recommendation: {f.recommendation}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── Delete Confirmation Dialog ──────────────────────────────────

function DeleteConfirmDialog({
  open,
  onOpenChange,
  target,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: WalkthroughRow | null;
  onConfirm: () => void;
  isPending: boolean;
}) {
  if (!target) return null;
  const totalFindings = target.findingCount ?? 0;
  const hasActive = target.status === "running" || target.status === "pending";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete walkthrough?</AlertDialogTitle>
          <AlertDialogDescription>
            {hasActive ? (
              "This walkthrough is still running. Cancel it before deleting."
            ) : totalFindings > 0 ? (
              <>
                This will permanently delete this walkthrough and its{" "}
                {totalFindings} finding{totalFindings !== 1 ? "s" : ""}.
                Cross-walkthrough patterns are preserved.
              </>
            ) : (
              "This will permanently delete this walkthrough. This action cannot be undone."
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isPending || hasActive}
          >
            {isPending ? "Deleting\u2026" : "Delete"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Page Component ──────────────────────────────────────────────

type StatusFilter = "all" | "completed" | "running" | "failed";
type ExtraWalkthroughFilter = "persona" | "scenario" | "build";

export function ProjectWalkthroughsPage() {
  const project = useProject();
  const navigate = useNavigate();
  const [showReleaseSummary, setShowReleaseSummary] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WalkthroughRow | null>(null);
  const [selectedRunIds, setSelectedRunIds] = useState<Set<string>>(new Set());
  const canEdit = useCanEdit();
  const emptyDescription = useEditorCopy(
    "Walkthroughs test your scenarios as personas and produce findings. Create scenarios first, then run walkthroughs to evaluate your product.",
    "Walkthroughs test your scenarios as personas and produce findings. Ask an editor to run the first one.",
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const sortParam = searchParams.get("sort");
  const sortOrder: "newest" | "oldest" =
    sortParam === "oldest" ? "oldest" : "newest";

  const statusParam = searchParams.get("status");
  const filterStatus: StatusFilter =
    statusParam === "completed" ||
    statusParam === "running" ||
    statusParam === "failed"
      ? statusParam
      : "all";

  const filterPersona = searchParams.get("persona") ?? "all";
  const filterScenario = searchParams.get("scenario") ?? "all";
  const filterBuild = searchParams.get("build") ?? "all";

  const qParam = searchParams.get("q") ?? "";
  const [searchQuery, setSearchQuery] = useState(qParam);

  useEffect(() => {
    setSearchQuery(qParam);
  }, [qParam]);

  const [extraVisibleFilters, setExtraVisibleFilters] = useState<
    Set<ExtraWalkthroughFilter>
  >(new Set());

  const visibleFilters = useMemo(() => {
    const s = new Set(extraVisibleFilters);
    if (filterPersona !== "all") s.add("persona");
    if (filterScenario !== "all") s.add("scenario");
    if (filterBuild !== "all") s.add("build");
    return s;
  }, [extraVisibleFilters, filterPersona, filterScenario, filterBuild]);

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(patch)) {
            if (val === null || val === "" || val === "all") {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      updateParams({ q: value.trim() ? value : null });
    },
    [updateParams],
  );

  const handleStatusChange = useCallback(
    (status: StatusFilter) => {
      updateParams({ status: status === "all" ? null : status });
    },
    [updateParams],
  );

  const handlePersonaChange = useCallback(
    (persona: string) => {
      updateParams({ persona: persona === "all" ? null : persona });
    },
    [updateParams],
  );

  const handleScenarioChange = useCallback(
    (scenario: string) => {
      updateParams({ scenario: scenario === "all" ? null : scenario });
    },
    [updateParams],
  );

  const handleBuildChange = useCallback(
    (build: string) => {
      updateParams({ build: build === "all" ? null : build });
    },
    [updateParams],
  );

  const handleSortChange = useCallback(
    (order: "newest" | "oldest") => {
      updateParams({ sort: order === "newest" ? null : order });
    },
    [updateParams],
  );

  const addFilter = useCallback((f: ExtraWalkthroughFilter) => {
    setExtraVisibleFilters((prev) => new Set(prev).add(f));
  }, []);

  const removeFilter = useCallback(
    (f: ExtraWalkthroughFilter) => {
      setExtraVisibleFilters((prev) => {
        const next = new Set(prev);
        next.delete(f);
        return next;
      });
      if (f === "persona") updateParams({ persona: null });
      if (f === "scenario") updateParams({ scenario: null });
      if (f === "build") updateParams({ build: null });
    },
    [updateParams],
  );

  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setExtraVisibleFilters(new Set());
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("q");
        next.delete("status");
        next.delete("persona");
        next.delete("scenario");
        next.delete("build");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const utils = trpc.useUtils();

  const deleteMutation = trpc.walkthrough.delete.useMutation({
    onSuccess: () => {
      toast.success("Walkthrough deleted");
      setDeleteTarget(null);
      utils.walkthrough.listByProject.invalidate({ projectId: project.id });
      utils.scenario.list.invalidate({ projectId: project.id });
      utils.finding.list.invalidate({ projectId: project.id });
      utils.finding.queueCounts.invalidate({ projectId: project.id });
      utils.activity.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to delete walkthrough"),
  });

  const {
    data: walkthroughs,
    isLoading,
    isError,
    refetch,
  } = trpc.walkthrough.listByProject.useQuery(
    { projectId: project.id },
    {
      refetchInterval: (query) => {
        const data = query.state.data;
        if (!data) return false;
        const hasActive = data.some(
          (w) => w.status === "running" || w.status === "pending",
        );
        return hasActive ? 3000 : false;
      },
    },
  );

  // Every run of each scenario, regardless of filters — the trend in each
  // group header is the scenario's whole history.
  const runsByScenario = useMemo(() => {
    const map = new Map<string, WalkthroughRow[]>();
    for (const w of (walkthroughs as unknown as WalkthroughRow[] | undefined) ??
      []) {
      const runs = map.get(w.scenarioId) ?? [];
      runs.push(w);
      map.set(w.scenarioId, runs);
    }
    return map;
  }, [walkthroughs]);

  const runNumbers = useMemo(() => {
    const map = new Map<string, number>();
    for (const runs of runsByScenario.values()) {
      const numbered = numberRuns(runs);
      for (const [id, num] of numbered.entries()) {
        map.set(id, num);
      }
    }
    return map;
  }, [runsByScenario]);

  const filteredWalkthroughs = useMemo(() => {
    if (!walkthroughs) return [] as WalkthroughRow[];
    const q = searchQuery.trim().toLowerCase();
    return (walkthroughs as unknown as WalkthroughRow[]).filter((w) => {
      if (q) {
        const matchesScenario = w.scenarioName?.toLowerCase().includes(q);
        const matchesPersona = w.personaName?.toLowerCase().includes(q);
        const matchesMode = w.mode?.toLowerCase().includes(q);
        const matchesBuild = w.buildLabel?.toLowerCase().includes(q);
        if (
          !matchesScenario &&
          !matchesPersona &&
          !matchesMode &&
          !matchesBuild
        )
          return false;
      }
      if (filterStatus !== "all") {
        if (filterStatus === "running") {
          if (w.status !== "running" && w.status !== "pending") return false;
        } else if (w.status !== filterStatus) return false;
      }
      if (
        visibleFilters.has("persona") &&
        filterPersona !== "all" &&
        w.personaName !== filterPersona
      ) {
        return false;
      }
      if (
        visibleFilters.has("scenario") &&
        filterScenario !== "all" &&
        w.scenarioId !== filterScenario
      ) {
        return false;
      }
      if (
        visibleFilters.has("build") &&
        filterBuild !== "all" &&
        w.buildId !== filterBuild
      ) {
        return false;
      }
      return true;
    });
  }, [
    walkthroughs,
    searchQuery,
    filterStatus,
    filterPersona,
    filterScenario,
    filterBuild,
    visibleFilters,
  ]);

  const personaOptions = useMemo(() => {
    const names = new Set<string>();
    for (const w of (walkthroughs as unknown as WalkthroughRow[] | undefined) ??
      []) {
      if (w.personaName) names.add(w.personaName);
    }
    return [...names].sort();
  }, [walkthroughs]);

  const scenarioOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of (walkthroughs as unknown as WalkthroughRow[] | undefined) ??
      []) {
      if (!map.has(w.scenarioId)) map.set(w.scenarioId, w.scenarioName);
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [walkthroughs]);

  const buildOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of (walkthroughs as unknown as WalkthroughRow[] | undefined) ??
      []) {
      if (w.buildId && !map.has(w.buildId)) {
        map.set(w.buildId, w.buildLabel ?? w.buildId.slice(0, 8));
      }
    }
    return [...map.entries()].map(([id, label]) => ({ id, label }));
  }, [walkthroughs]);

  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    filterStatus !== "all" ||
    (visibleFilters.has("persona") && filterPersona !== "all") ||
    (visibleFilters.has("scenario") && filterScenario !== "all") ||
    (visibleFilters.has("build") && filterBuild !== "all");

  const scenarioGroups = useMemo(() => {
    const groups = groupByScenario(filteredWalkthroughs);
    if (sortOrder === "oldest") {
      groups.reverse();
      for (const g of groups) g.walkthroughs.reverse();
    }
    return groups;
  }, [filteredWalkthroughs, sortOrder]);

  const latestCompletedWalkthroughIds = useMemo(() => {
    const ids: string[] = [];
    for (const group of scenarioGroups) {
      const latestCompleted = group.walkthroughs.find(
        (w) => w.status === "completed",
      );
      if (latestCompleted) {
        ids.push(latestCompleted.id);
      }
    }
    return ids;
  }, [scenarioGroups]);

  const handleToggleRunSelect = useCallback((id: string, checked: boolean) => {
    setSelectedRunIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        if (next.size >= 2) {
          const arr = Array.from(next);
          next.delete(arr[0]!);
        }
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleCompareRuns = useCallback(() => {
    if (selectedRunIds.size !== 2) return;
    const [idA, idB] = Array.from(selectedRunIds);
    const runA = (
      walkthroughs as unknown as WalkthroughRow[] | undefined
    )?.find((w) => w.id === idA);
    const runB = (
      walkthroughs as unknown as WalkthroughRow[] | undefined
    )?.find((w) => w.id === idB);
    if (!runA || !runB) return;

    const timeA = new Date(runA.createdAt).getTime();
    const timeB = new Date(runB.createdAt).getTime();
    const baseRun = timeA <= timeB ? runA : runB;
    const targetRun = timeA <= timeB ? runB : runA;

    navigate(
      `/projects/${project.id}/walkthroughs/${targetRun.id}?tab=compare&base=${baseRun.id}`,
    );
  }, [selectedRunIds, walkthroughs, project.id, navigate]);

  return (
    <Page
      title="Walkthroughs"
      actions={
        latestCompletedWalkthroughIds.length >= 2 ||
        (features.liveWalkthrough && canEdit) ? (
          <div className="flex items-center gap-2">
            {latestCompletedWalkthroughIds.length >= 2 && (
              <Button
                variant="outline"
                onClick={() => setShowReleaseSummary(true)}
              >
                <FileText className="mr-2 h-4 w-4" />
                Release summary ({latestCompletedWalkthroughIds.length})
              </Button>
            )}
            {features.liveWalkthrough && canEdit && (
              <Button asChild>
                <Link to={`/projects/${project.id}/live-walkthrough`}>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Run walkthrough
                </Link>
              </Button>
            )}
          </div>
        ) : undefined
      }
    >
      <WalkthroughsTabs projectId={project.id} current="runs" />
      <LimitReachedBanner resource="storage" className="mb-4" />
      <LimitReachedBanner resource="walkthroughs" className="mb-4" />
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <EntityErrorState
          title="Failed to load walkthroughs"
          description="We couldn't retrieve the walkthrough runs for this project. Please try again."
          onRetry={() => refetch()}
          embedded
        />
      ) : !walkthroughs || walkthroughs.length === 0 ? (
        <EmptyState
          icon={PlayCircle}
          title="No walkthroughs yet"
          description={emptyDescription}
          actions={
            <Button asChild>
              <Link to={`/projects/${project.id}/scenarios`}>
                Go to scenarios
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="space-y-10">
          <TableToolbar
            hasActiveFilters={hasActiveFilters}
            onResetFilters={resetFilters}
            count={
              walkthroughs.length > 0 ? (
                filteredWalkthroughs.length !== walkthroughs.length ? (
                  <span>
                    <strong className="text-foreground">
                      {filteredWalkthroughs.length}
                    </strong>{" "}
                    of {walkthroughs.length} run
                    {walkthroughs.length !== 1 ? "s" : ""}
                  </span>
                ) : (
                  <span>
                    <strong className="text-foreground">
                      {walkthroughs.length}
                    </strong>{" "}
                    run{walkthroughs.length !== 1 ? "s" : ""}
                  </span>
                )
              ) : undefined
            }
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {selectedRunIds.size > 0 && (
                  <BatchActionBar
                    variant="inline"
                    count={selectedRunIds.size}
                    noun="run"
                    onClear={() => setSelectedRunIds(new Set())}
                  >
                    {selectedRunIds.size === 1 ? (
                      <span className="text-xs text-muted-foreground px-2">
                        Select 1 more run to compare
                      </span>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        className="h-7 rounded-sm px-3 text-xs font-semibold bg-foreground text-background shadow-xs hover:bg-foreground/90 gap-1.5"
                        onClick={handleCompareRuns}
                      >
                        <GitCompare className="h-3.5 w-3.5" />
                        Compare 2 runs
                      </Button>
                    )}
                  </BatchActionBar>
                )}
                <SortDropdown
                  options={WALKTHROUGH_SORT_OPTIONS}
                  value={sortOrder}
                  onChange={handleSortChange}
                />
              </div>
            }
          >
            <TableSearchInput
              placeholder="Search walkthroughs..."
              value={searchQuery}
              onChange={handleSearchChange}
              ariaLabel="Search walkthroughs"
            />

            <FilterPill
              ariaLabel="Filter by status"
              value={filterStatus}
              onValueChange={(v) => handleStatusChange(v as StatusFilter)}
              options={[
                { value: "all", label: "All statuses" },
                { value: "completed", label: "Completed" },
                { value: "running", label: "Running" },
                { value: "failed", label: "Failed" },
              ]}
            />

            {visibleFilters.has("persona") && (
              <FilterPill
                ariaLabel="Filter by persona"
                placeholder="Persona"
                value={filterPersona}
                onValueChange={handlePersonaChange}
                onRemove={() => removeFilter("persona")}
                options={[
                  { value: "all", label: "All personas" },
                  ...personaOptions.map((name) => ({
                    value: name,
                    label: name,
                  })),
                ]}
              />
            )}

            {visibleFilters.has("scenario") && (
              <FilterPill
                ariaLabel="Filter by scenario"
                placeholder="Scenario"
                value={filterScenario}
                onValueChange={handleScenarioChange}
                onRemove={() => removeFilter("scenario")}
                maxWidth="max-w-[240px]"
                options={[
                  { value: "all", label: "All scenarios" },
                  ...scenarioOptions.map((s) => ({
                    value: s.id,
                    label: s.name,
                  })),
                ]}
              />
            )}

            {visibleFilters.has("build") && (
              <FilterPill
                ariaLabel="Filter by build"
                placeholder="Build"
                value={filterBuild}
                onValueChange={handleBuildChange}
                onRemove={() => removeFilter("build")}
                maxWidth="max-w-[240px]"
                options={[
                  { value: "all", label: "All builds" },
                  ...buildOptions.map((b) => ({
                    value: b.id,
                    label: b.label,
                  })),
                ]}
              />
            )}

            <AddFilterMenu
              options={[
                { id: "persona", label: "Persona" },
                { id: "scenario", label: "Scenario" },
                { id: "build", label: "Build" },
              ]}
              excluded={visibleFilters}
              onSelect={(id) => addFilter(id)}
            />
          </TableToolbar>
          {scenarioGroups.length === 0 ? (
            <FilteredEmptyState
              title="No walkthroughs match these filters"
              description="No walkthrough runs match your search query or filter selection."
              onReset={resetFilters}
            />
          ) : null}
          {scenarioGroups.map((group) => {
            const criticalityLabel = group.scenarioTaskCriticality
              ? vocabCriticalityLabel(group.scenarioTaskCriticality)
              : null;

            return (
              <section
                key={group.scenarioId}
                id={`scenario-${group.scenarioId}`}
                aria-label={`Scenario: ${group.scenarioName}`}
                className="space-y-2 scroll-mt-8"
              >
                <div className="rounded-lg border border-border/80 bg-card overflow-hidden shadow-xs dark:shadow-[inset_0_1px_0_0_oklch(1_0_0_/_0.06),0_6px_24px_-6px_oklch(0_0_0_/_0.4)]">
                  {/* Scenario run group header */}
                  <header className="space-y-2 border-b border-border/70 p-4 bg-muted/15">
                    <div className="flex items-baseline justify-between gap-4">
                      <h2 className="min-w-0 text-md font-semibold">
                        <Link
                          to={`/projects/${project.id}/scenarios/${group.outlineId}`}
                          className="transition-colors hover:text-primary"
                        >
                          {group.scenarioName}
                        </Link>
                      </h2>
                      <div className="flex items-center gap-2.5 shrink-0">
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {group.walkthroughs.length}{" "}
                          {group.walkthroughs.length === 1 ? "run" : "runs"}
                        </span>
                        {canEdit &&
                          (features.liveWalkthrough ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2.5 text-xs font-medium gap-1.5 shadow-2xs hover:bg-muted"
                              asChild
                            >
                              <Link
                                to={`/projects/${project.id}/live-walkthrough?scenarioId=${group.scenarioId}${group.personas[0]?.id ? `&personaId=${group.personas[0].id}` : ""}`}
                                title={`Run new walkthrough for ${group.scenarioName}`}
                                aria-label={`Run walkthrough for scenario: ${group.scenarioName}`}
                              >
                                <PlayCircle className="h-3.5 w-3.5 text-primary" />
                                <span>Run</span>
                              </Link>
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2.5 text-xs font-medium gap-1.5 shadow-2xs hover:bg-muted"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `iamu run ${group.scenarioId}`,
                                );
                                toast.success(
                                  "Scenario run command copied to clipboard",
                                );
                              }}
                              title={`Copy CLI run command: iamu run ${group.scenarioId}`}
                              aria-label={`Run walkthrough for scenario: ${group.scenarioName}`}
                            >
                              <PlayCircle className="h-3.5 w-3.5 text-primary" />
                              <span>Run</span>
                            </Button>
                          ))}
                      </div>
                    </div>
                    {(criticalityLabel ||
                      group.personas.length > 0 ||
                      group.scenarioTension) && (
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        {criticalityLabel && group.scenarioTaskCriticality && (
                          <Chip
                            tone={toneChipTone(
                              criticalityTone(group.scenarioTaskCriticality),
                            )}
                          >
                            {criticalityLabel}
                          </Chip>
                        )}
                        {group.personas.map((persona) => {
                          const key = persona.id ?? persona.name;
                          return (
                            <PersonaRow
                              key={key}
                              variant="pill"
                              persona={{
                                id: persona.id ?? persona.name,
                                name: persona.name,
                              }}
                              href={
                                persona.id
                                  ? `/projects/${project.id}/personas/${persona.id}`
                                  : undefined
                              }
                              size="xs"
                            />
                          );
                        })}
                        {group.scenarioTension && (
                          <span className="min-w-0 truncate pl-1 text-sm text-muted-foreground">
                            {group.scenarioTension}
                          </span>
                        )}
                      </div>
                    )}
                    <RunTrend
                      runs={runsByScenario.get(group.scenarioId) ?? []}
                      projectId={project.id}
                      runNumbers={runNumbers}
                      className="pt-1"
                    />
                    {group.latestVerdict && (
                      <p className="max-w-[80ch] line-clamp-1 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">
                          Latest run:
                        </span>{" "}
                        <span>{group.latestVerdict}</span>
                      </p>
                    )}
                  </header>

                  {/* Walkthrough rows */}
                  <ul className="divide-y divide-border/60">
                    {group.walkthroughs.map((wt) => {
                      const run = runNumbers.get(wt.id);
                      const hasMultiplePersonas = group.personas.length > 1;
                      const baseLabel = run
                        ? hasMultiplePersonas && wt.personaName
                          ? `Run #${run} · ${wt.personaName}`
                          : `Run #${run}`
                        : (wt.personaName ?? "Run");
                      const label = wt.buildLabel
                        ? `${baseLabel} · ${wt.buildLabel}`
                        : baseLabel;

                      const path = `/projects/${project.id}/walkthroughs/${wt.id}`;

                      return (
                        <li
                          key={wt.id}
                          className="group flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-muted/20"
                        >
                          <Checkbox
                            checked={selectedRunIds.has(wt.id)}
                            onCheckedChange={(checked) =>
                              handleToggleRunSelect(wt.id, !!checked)
                            }
                            aria-label={`Select ${label} for comparison`}
                            className={cn(
                              "h-4 w-4 shrink-0 transition-opacity",
                              selectedRunIds.size > 0
                                ? "opacity-100"
                                : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                            )}
                          />
                          <Link to={path} className="flex-1 min-w-0 block">
                            <WalkthroughItemContent
                              walkthrough={wt}
                              showPersona={false}
                              label={label}
                            />
                          </Link>
                          {canEdit && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                                  aria-label={`Actions for ${label}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  variant="destructive"
                                  onClick={() => setDeleteTarget(wt)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete walkthrough
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Release Summary Sheet */}
      <Sheet open={showReleaseSummary} onOpenChange={setShowReleaseSummary}>
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Release summary</SheetTitle>
            <SheetDescription>
              Synthesized summary across {latestCompletedWalkthroughIds.length}{" "}
              latest scenario runs
            </SheetDescription>
          </SheetHeader>
          <ReleaseSummaryContent
            walkthroughIds={latestCompletedWalkthroughIds}
            projectId={project.id}
          />
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={canEdit && !!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        target={deleteTarget}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate({ ids: [deleteTarget.id] });
          }
        }}
        isPending={deleteMutation.isPending}
      />
    </Page>
  );
}
